import { UploadIcon } from "@heroicons/react/outline";
import { useCreate } from "@refinedev/core";
import { Loader } from "components/loader";
import { isEmpty } from "lodash";
import React from "react";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import { bytesForHuman, isInvalidImageSize } from "utills/helpers";

interface PropType {
  id?: string;
  value?: string[];
  onChange?: (data: { url: string; file: File }) => void;
  [x: string]: any;
}

const ImportHtmlButton = React.forwardRef(
  ({ onChange }: PropType, ref: any) => {
    const { mutate, isLoading: isLoading } = useCreate();

    const _uploadFiles = async (file: File) => {
      const formData = new FormData();
      formData.append("upload", file, `${file.name}`);
      return new Promise<string>((resolve, reject) => {
        mutate(
          {
            dataProviderName: "externalUpload",
            resource: "/",
            values: formData,
          },
          {
            onSuccess: (data) => {
              const fileUrl = data.data?.result.img_url;
              resolve(fileUrl || "");
            },
            onError: () => {
              reject("Upload failed");
            },
          }
        );
      });
    };

    const {
      getRootProps: getRootkProps,
      getInputProps,
      open: openUpload,
    } = useDropzone({
      noClick: true,
      noKeyboard: true,
      multiple: false,
      accept: {
        "text/html": [".html"],
      },
      validator: (file: File) => {
        const maximumSize = 3145728; // 3 MB
        if (isInvalidImageSize(file, maximumSize)) {
          return {
            code: "invalid-size",
            message: `Dung lượng vượt quá giới hạn ${bytesForHuman(
              maximumSize
            )}.`,
          };
        }
        return null;
      },
      onDrop: async (acceptedFiles, rejectedFiles) => {
        if (acceptedFiles.length > 0 && isEmpty(rejectedFiles)) {
          for (let i = 0; i < acceptedFiles.length; i++) {
            const file = acceptedFiles[i];
            const fileUrl = await _uploadFiles(file);
            if (!fileUrl) return;
            onChange?.({ url: fileUrl, file });
          }
        }
      },
      onDropRejected: (errorFiles) => {
        if (!isEmpty(errorFiles)) {
          const errorCode = errorFiles[0].errors[0].code;
          if (errorCode === "file-invalid-type") {
            return toast.error(`Chỉ hỗ trợ tệp có đuôi .html.`);
          }
          return toast.error(errorFiles[0].errors[0].message);
        }
      },
    });

    return (
      <section className="w-full h-full">
        <div {...getRootkProps({ className: "h-full" })}>
          <div
            className="relative h-auto overflow-hidden rounded-[4px]
                        bg-[#011BB6] text-white px-[18px] py-[14px]"
          >
            {isLoading ? (
              <div
                className="opacity-100 bg-gray-900/40 duration-300 absolute
                            inset-0 flex justify-center items-center rounded-[4px]"
              >
                <Loader size={40} />
              </div>
            ) : null}

            <div className="w-full h-full">
              <input {...getInputProps()} />
              <button
                type="button"
                onClick={openUpload}
                className="text-[16px] font-[500] flex items-center gap-[8px]"
              >
                <UploadIcon className="size-[24px]" />
                <span>Import HTML File</span>
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }
);

ImportHtmlButton.displayName = "ImportHtmlButton";

export default ImportHtmlButton;
